/**
 * The desktop "Add ESC/POS printer" flow: scan or type an address → check →
 * test page → save (and optionally use).
 *
 * Rules the hook keeps, whatever the panels do:
 * - A scan lists **candidates**. An open port is not a printer; a host that
 *   answered the status question is "identified"; only an explicit test
 *   print makes it "confirmed". Nothing is saved by scanning or testing.
 * - The scan is bounded and cancellable, and when the register sits on more
 *   than one network the operator picks which one to search.
 * - The port is editable (9100 by default) and travels with the address.
 * - Checking a candidate opens a managed connection in the shell; leaving
 *   the step without saving releases it, so a trial never becomes the
 *   register's printer by accident.
 * - Every step is printer-only. Nothing here touches payment state.
 */
import { useRef, useState, useEffect, useCallback } from 'react';

import { usePrinterStore } from 'src/store/PrinterStore';
import { useDeviceSyncStore } from 'src/store/DeviceSync';
import { PrintType } from 'src/services/printing/print-types';
import { renderToBase64 } from 'src/services/printing/renderer-registry';
import type {
  EscPosCandidate,
  EscPosSerialPort,
  EscPosWindowsQueue,
  EscPosScanInterface,
  EscPosAttachedDevices,
} from 'src/platform/services';
import {
  printerService,
  newEscPosPrinter,
  escPosTransportOf,
  type PrinterStatus,
  type PrintJobResult,
  newEscPosQueuePrinter,
  escPosQueueIdentifier,
  newEscPosSerialPrinter,
  escPosSerialIdentifier,
  ESCPOS_PROFILE_PRESETS,
  type SavedEscPosPrinter,
  type EscPosProfileInput,
  type EscPosLanConnection,
  type EscPosSerialConnection,
  DEFAULT_ESCPOS_SERIAL_BAUD_RATE,
} from 'src/services/printers';

import { checkEscPosAddress, ESCPOS_DEFAULT_PORT } from './escpos-address';

export type EscPosSetupMode = 'scan' | 'manual' | 'attached';

export interface EscPosScanState {
  readonly phase: 'idle' | 'choose-interface' | 'searching' | 'done' | 'failed';
  readonly candidates: readonly EscPosCandidate[];
  readonly interfaces: readonly EscPosScanInterface[];
  readonly notes: readonly string[];
  readonly progress: { completed: number; total: number } | null;
  readonly cancelled: boolean;
  readonly error?: string;
}

export interface EscPosDevicesState {
  readonly phase: 'idle' | 'loading' | 'done' | 'failed';
  readonly result: EscPosAttachedDevices | null;
  readonly error?: string;
}

export interface EscPosCandidateState {
  readonly printer: SavedEscPosPrinter;
  readonly source: 'scan' | 'manual' | 'attached';
  readonly identified: boolean;
  readonly check: 'idle' | 'checking' | 'reachable' | 'unreachable';
  readonly status?: PrinterStatus;
  readonly checkError?: string;
  readonly test: 'idle' | 'printing' | 'printed' | 'failed';
  readonly testResult?: PrintJobResult;
  readonly testError?: string;
}

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function newScanId(): string {
  return `scan-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

const IDLE_SCAN: EscPosScanState = {
  phase: 'idle',
  candidates: [],
  interfaces: [],
  notes: [],
  progress: null,
  cancelled: false,
};

export function useEscPosPrinterSetup(onSaved: (printer: SavedEscPosPrinter, use: boolean) => void) {
  const business = useDeviceSyncStore((state) => state.businessData);
  const remembered = usePrinterStore((state) => state.rememberedPrinters);

  const [mode, setMode] = useState<EscPosSetupMode>('scan');
  const [port, setPort] = useState<string>(String(ESCPOS_DEFAULT_PORT));
  const [interfaceName, setInterfaceName] = useState<string | null>(null);
  const [scan, setScan] = useState<EscPosScanState>(IDLE_SCAN);
  const [addressInput, setAddressInput] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);
  const [candidate, setCandidate] = useState<EscPosCandidateState | null>(null);
  const [name, setName] = useState('');
  const [presetId, setPresetId] = useState(ESCPOS_PROFILE_PRESETS[0]?.id ?? 'generic-80mm');
  const [profile, setProfile] = useState<EscPosProfileInput>({});
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState<EscPosDevicesState>({ phase: 'idle', result: null });
  const [baudRate, setBaudRate] = useState<number>(DEFAULT_ESCPOS_SERIAL_BAUD_RATE);
  const activeScan = useRef<string | null>(null);
  /** A candidate connection was opened that no saved printer owns. */
  const openedCandidate = useRef<SavedEscPosPrinter | null>(null);

  const savedAddresses = remembered
    .filter(
      (printer): printer is SavedEscPosPrinter & { connection: EscPosLanConnection } =>
        printer.driver === 'escpos' && escPosTransportOf(printer) === 'lan'
    )
    .map((printer) => `${printer.connection.ipAddress}:${String(printer.connection.port)}`);

  // Scan progress arrives as events from the shell while a scan runs.
  useEffect(
    () =>
      printerService.escpos.onDiscoveryProgress((event) => {
        if (event.scanId !== activeScan.current) return;
        setScan((current) => ({ ...current, progress: { completed: event.completed, total: event.total } }));
      }),
    []
  );

  const parsedPort = (): number | null => {
    const value = Number.parseInt(port, 10);
    return Number.isInteger(value) && value >= 1 && value <= 65535 ? value : null;
  };

  const discover = useCallback(
    async (chosenInterface: string | null = interfaceName) => {
      const portNumber = parsedPort();
      if (portNumber === null) {
        setScan({ ...IDLE_SCAN, phase: 'failed', error: 'Enter a port between 1 and 65535 (usually 9100).' });
        return;
      }
      const scanId = newScanId();
      activeScan.current = scanId;
      setScan((current) => ({ ...current, phase: 'searching', error: undefined, cancelled: false, progress: null }));
      try {
        const result = await printerService.escpos.discover({
          scanId,
          port: portNumber,
          knownAddresses: savedAddresses,
          sweep: true,
          interfaceName: chosenInterface ?? undefined,
          timeoutMs: 20_000,
        });
        if (activeScan.current !== scanId) return; // superseded
        setScan({
          phase: result.diagnostics.requiresInterfaceChoice ? 'choose-interface' : 'done',
          candidates: result.candidates,
          interfaces: result.diagnostics.interfaces,
          notes: result.notes,
          progress: null,
          cancelled: result.diagnostics.cancelled,
        });
      } catch (error) {
        if (activeScan.current !== scanId) return;
        setScan({ ...IDLE_SCAN, phase: 'failed', error: messageOf(error, 'The search failed.') });
      } finally {
        if (activeScan.current === scanId) activeScan.current = null;
      }
    },
    // `port` and `savedAddresses` are read when the scan starts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [interfaceName, port, remembered]
  );

  const cancelScan = useCallback(async () => {
    const scanId = activeScan.current;
    if (scanId === null) return;
    await printerService.escpos.cancelDiscovery(scanId).catch(() => undefined);
  }, []);

  const chooseInterface = useCallback(
    (chosen: string) => {
      setInterfaceName(chosen);
      discover(chosen);
    },
    [discover]
  );

  /** Opens a managed connection to the candidate and records what it said. */
  const check = useCallback(
    async (printer: SavedEscPosPrinter, source: EscPosCandidateState['source'], identified: boolean) => {
      setCandidate({ printer, source, identified, check: 'checking', test: 'idle' });
      setName(printer.name ?? '');
      try {
        openedCandidate.current = printer;
        const { status } = await printerService.escpos.testConnection(printer);
        setCandidate({ printer, source, identified, check: 'reachable', status, test: 'idle' });
      } catch (error) {
        setCandidate({
          printer,
          source,
          identified,
          check: 'unreachable',
          checkError: messageOf(error, 'The printer did not answer.'),
          test: 'idle',
        });
      }
    },
    []
  );

  const candidatePrinter = useCallback(
    (ipAddress: string, portNumber: number): SavedEscPosPrinter => {
      const existing = remembered.find(
        (printer): printer is SavedEscPosPrinter =>
          printer.driver === 'escpos' &&
          escPosTransportOf(printer) === 'lan' &&
          (printer.connection as EscPosLanConnection).ipAddress === ipAddress &&
          (printer.connection as EscPosLanConnection).port === portNumber
      );
      if (existing) {
        setProfile(existing.profile);
        return existing;
      }
      setProfile({});
      return newEscPosPrinter({ ipAddress, port: portNumber });
    },
    [remembered]
  );

  const selectCandidate = useCallback(
    (found: EscPosCandidate) => check(candidatePrinter(found.ipAddress, found.port), 'scan', found.identified),
    [check, candidatePrinter]
  );

  // ── Attached printers: Windows print queues and COM ports ──────────────────

  const loadDevices = useCallback(async () => {
    setDevices({ phase: 'loading', result: null });
    try {
      const result = await printerService.escpos.listDevices();
      setDevices({ phase: 'done', result });
    } catch (error) {
      setDevices({ phase: 'failed', result: null, error: messageOf(error, 'Attached printers could not be listed.') });
    }
  }, []);

  const existingEscPos = useCallback(
    (identifier: string): SavedEscPosPrinter | undefined =>
      remembered.find(
        (printer): printer is SavedEscPosPrinter => printer.driver === 'escpos' && printer.identifier === identifier
      ),
    [remembered]
  );

  /** A Windows print queue: the primary USB path. Saved with everything Windows knows about the device. */
  const selectQueue = useCallback(
    (queue: EscPosWindowsQueue) => {
      const existing = existingEscPos(escPosQueueIdentifier(queue.name));
      const printer =
        existing ??
        newEscPosQueuePrinter({
          queueName: queue.name,
          driverName: queue.driverName,
          portName: queue.portName,
          pnpDeviceId: queue.pnpDeviceId,
          usb: queue.usb,
        });
      setProfile(existing?.profile ?? {});
      return check(printer, 'attached', false);
    },
    [check, existingEscPos]
  );

  /** A COM / virtual COM port: realtime status where the printer answers. */
  const selectSerialPort = useCallback(
    (serialPort: EscPosSerialPort) => {
      const existing = existingEscPos(escPosSerialIdentifier(serialPort.portName));
      const printer =
        existing ??
        newEscPosSerialPrinter({
          portName: serialPort.portName,
          baudRate,
          friendlyName: serialPort.friendlyName,
          pnpDeviceId: serialPort.pnpDeviceId,
          usb: serialPort.usb,
        });
      setProfile(existing?.profile ?? {});
      if (existing && escPosTransportOf(existing) === 'serial') {
        setBaudRate((existing.connection as EscPosSerialConnection).baudRate);
      }
      return check(printer, 'attached', false);
    },
    [check, existingEscPos, baudRate]
  );

  const checkAddress = useCallback(async () => {
    const parsed = checkEscPosAddress(addressInput, port);
    if (!parsed.ok) {
      setAddressError(parsed.message);
      return;
    }
    setAddressError(null);
    await check(candidatePrinter(parsed.ipAddress, parsed.port), 'manual', false);
  }, [addressInput, port, check, candidatePrinter]);

  /** The printer record as it would be saved: endpoint + current name + profile (+ baud for serial). */
  const draft = useCallback((): SavedEscPosPrinter | null => {
    if (candidate === null) return null;
    const trimmed = name.trim();
    const connection =
      escPosTransportOf(candidate.printer) === 'serial'
        ? { ...(candidate.printer.connection as EscPosSerialConnection), baudRate }
        : candidate.printer.connection;
    return {
      ...candidate.printer,
      ...(trimmed.length > 0 ? { name: trimmed } : {}),
      connection,
      profile,
    };
  }, [candidate, name, profile, baudRate]);

  const choosePreset = useCallback((id: string) => {
    setPresetId(id);
    setProfile(ESCPOS_PROFILE_PRESETS.find((preset) => preset.id === id)?.profile ?? {});
  }, []);

  const testPrint = useCallback(async () => {
    const printer = draft();
    if (candidate === null || printer === null) return;
    setCandidate({ ...candidate, test: 'printing', testError: undefined });
    try {
      const image = await renderToBase64(PrintType.TEST, { business: business ?? undefined });
      const result = await printerService.printTestImageTo(printer, image);
      if (!result.success) {
        throw new Error(result.error?.message ?? result.message ?? 'The test page did not print.');
      }
      setCandidate({ ...candidate, check: 'reachable', test: 'printed', testResult: result });
    } catch (error) {
      setCandidate({ ...candidate, test: 'failed', testError: messageOf(error, 'The test page did not print.') });
    }
  }, [business, candidate, draft]);

  /**
   * Saves the candidate. "Save" remembers it; "Save and use" also makes it the
   * active printer. The test-page-first rule is a UI gate the operator may
   * explicitly override; the destination rule in the shell is not.
   */
  const save = useCallback(
    async (use: boolean) => {
      const printer = draft();
      if (printer === null) return false;
      setSaving(true);
      try {
        // Once saved, the trial connection belongs to a remembered printer.
        openedCandidate.current = null;
        setCandidate(null);
        onSaved(printer, use);
        return true;
      } finally {
        setSaving(false);
      }
    },
    [draft, onSaved]
  );

  /** Leaves the check step without saving; the trial connection is released. */
  const discardCandidate = useCallback(async () => {
    setCandidate(null);
    const opened = openedCandidate.current;
    if (opened === null) return;
    openedCandidate.current = null;
    const isSaved = remembered.some((printer) => printer.identifier === opened.identifier);
    if (!isSaved) await printerService.escpos.release(opened).catch(() => undefined);
  }, [remembered]);

  const changeMode = useCallback((next: EscPosSetupMode) => {
    setMode(next);
    setAddressError(null);
  }, []);

  return {
    mode,
    changeMode,
    port,
    setPort,
    interfaceName,
    chooseInterface,
    scan,
    discover: () => discover(),
    cancelScan,
    addressInput,
    setAddressInput,
    addressError,
    checkAddress,
    candidate,
    selectCandidate,
    devices,
    loadDevices,
    selectQueue,
    selectSerialPort,
    baudRate,
    setBaudRate,
    name,
    setName,
    presetId,
    choosePreset,
    profile,
    setProfile,
    testPrint,
    save,
    saving,
    discardCandidate,
  };
}

export type EscPosPrinterSetup = ReturnType<typeof useEscPosPrinterSetup>;
